/* oxlint-disable promise/prefer-await-to-then */
import {
  StrictMode,
  Suspense,
  use,
  type ComponentPropsWithoutRef,
  type JSX,
} from "react";

import { i18n } from "@lingui/core";
import {
  initSentry,
  reactErrorHandler,
  withScope,
} from "@mobsuccess-devops/react-sentry";
import type { InitSentryOptions } from "@mobsuccess-devops/react-sentry";
import { ErrorBoundary } from "@mobsuccess-devops/react-ui/ErrorBoundary";
import { ErrorPage } from "@mobsuccess-devops/react-ui/ErrorPage";
import { Loader } from "@mobsuccess-devops/react-ui/Loader";
import { Toaster } from "@mobsuccess-devops/react-ui/Toaster";
import { setTag } from "@sentry/react";
import {
  TanStackDevtools,
  type TanStackDevtoolsReactPlugin,
} from "@tanstack/react-devtools";
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";

import { createRoot } from "react-dom/client";

import { SessionProvider } from "../components/Auth/SessionProvider";
import { AxiosContextProvider } from "../components/AxiosContextProvider/AxiosContextProvider";
import { EnsureUser } from "../components/EnsureUser/EnsureUser";
import FlashMessages from "../components/FlashMessages";
import { MutationErrorToaster } from "../components/MutationErrorToaster/mutation-error-toaster";
import { NotifyVersionDrift } from "../components/NotifyVersionDrift/notify-version-drift";
import { configure, prefetchSession } from "../public/auth/auth";
import { ForbiddenError, NotFoundError } from "../public/errors";
import { Localized } from "../public/i18n/Localized";
import { activate, buildLocales, type LocaleModule } from "../public/i18n/i18n";
import { isRetriableQueryError } from "../public/tanstack-query/http-error";
import { setUploadService } from "../public/upload/service";
import type { BusinessUnits } from "../types/enums";

import { AxiosInstances, type AxiosInstancesConfig } from "./axios-instances";
import { getCaughtErrorReport } from "./caught-error-report";
import { isChunkLoadError } from "./chunk-load-error";
import { notifyMutationError } from "./mutation-error-toast";
import { recoverFromChunkLoadError } from "./recover-from-chunk-load-error";

const QUERY_RETRY_DELAYS_MS = [1000, 2000, 4000];

export const queryClient = new QueryClient({
  // A mutation that declares its own onError owns its error UX; every other
  // failed mutation gets a toast, so a rejected action is never silent (the
  // observed alternative is the user re-clicking a button that will never
  // succeed, one Sentry event per click).
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError) {
        return;
      }
      notifyMutationError(error);
    },
  }),
  defaultOptions: {
    queries: {
      // Queries feed assertQuery call sites, where a single failure blanks the
      // whole route: retry the failures that are purely transient (offline
      // blip, 5xx, 429) and keep failing fast on everything else. ~7s total:
      // long enough to bridge a connection drop the browser did not detect
      // (VPN hiccup, wifi switch), short enough that a genuinely dead network
      // reaches the error page instead of an endless spinner. True offline is
      // handled by networkMode "online" (pause) + refetchOnReconnect, and
      // sub-second blips by the axios transient retry.
      retry: (failureCount, error) =>
        failureCount < QUERY_RETRY_DELAYS_MS.length &&
        isRetriableQueryError(error),
      retryDelay: (attempt) => QUERY_RETRY_DELAYS_MS[attempt] ?? 4000,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

export type CreateRootOptions = {
  businessUnit: BusinessUnits;
  sentry?: InitSentryOptions;
  entrypoint: string;
  lingui: {
    defaultLanguage: string;
    locales: Record<string, LocaleModule>;
  };
  auth?: {
    userPoolsWebClientId: string;
    useIdentityPoolId?: boolean;
    // Injected by the auth webapp only: hosts the full login flow around the
    // app. Every other webapp leaves this unset and gets the session-only
    // provider path. Passing the component by reference (instead of importing
    // AuthProvider here) is what keeps react-shared free of a dependency on
    // the auth app's login stack.
    AuthLayer?: React.ComponentType<{
      children: JSX.Element;
      axiosInstances: AxiosInstances;
    }>;
  };
  axios?: AxiosInstancesConfig;
  disableEnsureUser?: boolean;
  disableExternalTranslations?: boolean;
  children: JSX.Element;
  devtoolsPlugins?: TanStackDevtoolsReactPlugin[];
};

export async function createAppRoot({
  auth,
  axios,
  lingui,
  sentry,
  children,
  entrypoint,
  devtoolsPlugins = [],
  disableEnsureUser = false,
  disableExternalTranslations,
}: CreateRootOptions): Promise<void> {
  setUploadService(entrypoint);

  const root = window?.top?.document.getElementById(
    `react_root--${entrypoint}`,
  );
  if (!root) {
    throw new Error(`Could not find root element for ${entrypoint}`);
  }

  const body = window?.top?.document.body;
  if (disableExternalTranslations && body) {
    body.setAttribute("translate", "no");
  }

  const axiosInstances = new AxiosInstances(axios ?? {});

  const locales = buildLocales(lingui.locales);
  const localesPromise = activate(i18n, locales).then((resolvedLocale) => {
    document.documentElement.setAttribute("lang", resolvedLocale);
  });

  const configurePromise = configure({
    useIdentityPoolId: auth?.useIdentityPoolId,
  });
  const sessionPromise = auth
    ? configurePromise.then(() =>
        prefetchSession({
          queryClient,
          axiosInstances,
          userPoolsWebClientId: auth.userPoolsWebClientId,
          trustDevice: false,
          // Must agree with the provider that mounts below: both share the
          // session query key, and the first writer wins.
          validateSession: !auth.AuthLayer,
        }),
      )
    : configurePromise;

  const prefetchPromises = Promise.all([localesPromise, sessionPromise]);

  const PrefetchWrapper: React.FC<{
    children: JSX.Element;
  }> = ({ children }): JSX.Element => {
    use(prefetchPromises);
    return children;
  };

  type AuthWrapperProps = { children: JSX.Element };
  const AuthWrapper = ({ children }: AuthWrapperProps): JSX.Element => {
    if (auth?.AuthLayer) {
      return (
        <auth.AuthLayer axiosInstances={axiosInstances}>
          {disableEnsureUser ? (
            <>{children}</>
          ) : (
            <EnsureUser>{children}</EnsureUser>
          )}
        </auth.AuthLayer>
      );
    }
    if (auth) {
      return (
        <SessionProvider
          axiosInstances={axiosInstances}
          userPoolsWebClientId={auth.userPoolsWebClientId}
        >
          {disableEnsureUser ? (
            <>{children}</>
          ) : (
            <EnsureUser>{children}</EnsureUser>
          )}
        </SessionProvider>
      );
    }
    return <>{children}</>;
  };

  if (sentry) {
    initSentry(sentry);
    trackNetworkOnlineTag();
  }

  const devtools: ComponentPropsWithoutRef<typeof TanStackDevtools> = {
    plugins: [
      ...devtoolsPlugins,
      { name: "Query", render: <ReactQueryDevtoolsPanel /> },
    ],
    config: {
      triggerHidden: true,
      theme: "light" as const,
      openHotkey: ["Shift", "Control", "D"],
    },
  };

  return createRoot(root, {
    onUncaughtError: reactErrorHandler(),
    onCaughtError: handleCaughtError,
    onRecoverableError: reactErrorHandler(),
  }).render(
    <StrictMode>
      <ErrorBoundary.Root>
        <Suspense
          fallback={
            <Loader.Block>
              <Loader.Ellipsis />
            </Loader.Block>
          }
        >
          <PrefetchWrapper>
            <QueryClientProvider client={queryClient}>
              <TanStackDevtools {...devtools} />
              <Localized i18n={i18n}>
                <Toaster>
                  <MutationErrorToaster />
                  <NotifyVersionDrift entrypoint={entrypoint} />
                  <FlashMessages>
                    <AuthWrapper>
                      <AxiosContextProvider orchestrator={axiosInstances}>
                        {children}
                      </AxiosContextProvider>
                    </AuthWrapper>
                  </FlashMessages>
                </Toaster>
              </Localized>
            </QueryClientProvider>
          </PrefetchWrapper>
        </Suspense>
        <ErrorBoundary.Fallback>
          <ErrorPage.Any />
        </ErrorBoundary.Fallback>
      </ErrorBoundary.Root>
    </StrictMode>,
  );
}

const sentryCaughtErrorHandler = reactErrorHandler();

// ForbiddenError and NotFoundError are expected control flow: route guards
// throw them and ErrorComponent renders a dedicated page, so they must not be
// reported to Sentry.
function handleCaughtError(
  ...args: Parameters<ReturnType<typeof reactErrorHandler>>
): void {
  const [error, errorInfo] = args;
  if (error instanceof ForbiddenError || error instanceof NotFoundError) {
    return;
  }
  if (isChunkLoadError(error)) {
    // Fire and forget: the reload, if any, happens once the deployed revision
    // comes back. Reporting continues either way so that chunk failures which
    // are *not* a stale bundle stay visible.
    void recoverFromChunkLoadError();
  }
  const report = getCaughtErrorReport(error, window.location.pathname);
  withScope((scope) => {
    scope.setFingerprint(report.fingerprint);
    sentryCaughtErrorHandler(report.error, errorInfo);
  });
}

// `network.online` mirrors navigator.onLine so a "Network Error" event can be
// told apart from a server failure at a glance. Updated on every transition,
// so it reflects the state at capture time, not at page load.
function trackNetworkOnlineTag(): void {
  const update = (): void => {
    setTag("network.online", String(navigator.onLine));
  };
  update();
  window.addEventListener("online", update);
  window.addEventListener("offline", update);
}
